import { HEADLINE, attached, plainText } from "./notes";

// The overview (O): every board in a grid, as large as the screen lets it
// be, each with its place in the room and its name under it. Here the
// presenter hides a board or brings it back, and carries a board — or a
// chapter, by its opening page — to a new place (drag.js). The order is
// saved as it is let go; the deck is drawn again in it when the overview
// closes, since the folios and the chapters are the server's to number.

const BOARD_WIDTH = 960;
const BOARD_HEIGHT = 540;
// The grid's spacing, as drawn in deck/shell.css: the gaps between tiles,
// the name under a board, and the canvas's own padding.
const GAP_X = 16;
const GAP_Y = 20;
const CAPTION = 30;
const PAD_X = 24;
const PAD_Y = 40;
// Fitted, a board is no smaller than this: below it the grid scrolls
// instead. Sized by hand, − and + go between one column and the most that
// still leave a board this wide.
const FIT_MIN = 200;
const TILE_MIN = 120;
const TILE_MAX = 640;
const COLUMNS_KEY = "carttype:deck-overview-columns";

export default {
  // In the overview, a board is a button to itself.
  handlePick(event) {
    if (!this.overview) return;
    if (event.target.closest(".deck-tile-caption button")) return;
    const board = event.target
      .closest(".deck-tile")
      ?.querySelector('[data-presentation-target="slide"]');
    if (!board) return;
    const index = this.slideTargets.indexOf(board);
    if (index < 0) return;
    this.showSlide(this.nearestVisible(index));
    this.toggleOverview();
  },

  // Closing on a new order draws the deck again, on the board picked.
  toggleOverview() {
    if (!this.hasStageTarget || this.stacked) return;
    if (this.overview && this.reordered) return this.reloadHere();
    this.overview = !this.overview;
    this.stageTarget.classList.toggle("deck-stage--overview", this.overview);
    if (this.hasOverviewBarTarget)
      this.overviewBarTarget.hidden = !this.overview;
    if (this.hasOverviewButtonTarget) {
      this.overviewButtonTarget.setAttribute(
        "aria-pressed",
        String(this.overview),
      );
    }
    if (this.overview) {
      this.buildTiles();
      this.renderTiles();
      this.sizeTiles();
      this.hydrateAll();
      this.slideTargets[this.indexValue]
        ?.closest(".deck-tile")
        ?.scrollIntoView({ block: "center" });
    }
  },

  // The presenter can arrange the deck here: their own windows, not the
  // presenter window, a share link or a member's copy.
  get arranging() {
    return (
      this.hasOverviewBarTarget && "arrange" in this.overviewBarTarget.dataset
    );
  },

  // Each board in a tile with its name under it, made the first time the
  // overview opens. Outside the overview a tile is `display: contents`, so
  // the boards are the canvas's to place as they always were.
  buildTiles() {
    if (this.tiled) return;
    this.tiled = true;
    const bar = this.hasOverviewBarTarget ? this.overviewBarTarget.dataset : {};
    this.slideTargets.forEach((board) => {
      const slug = board.dataset.slideSlug || "";
      const tile = document.createElement("div");
      tile.className = "deck-tile";
      // A front page is carried with its board, as one slide's pages are.
      tile.dataset.key = board.dataset.front || slug.replace(/-\d+$/, "");
      if (board.dataset.chapter) tile.dataset.chapter = board.dataset.chapter;
      if ("opener" in board.dataset) tile.dataset.opener = "";
      if (slug === "cover") tile.dataset.cover = "";

      const caption = document.createElement("div");
      caption.className = "deck-tile-caption";
      const number = document.createElement("span");
      number.className = "deck-tile-number deck-label-sm";
      const name = document.createElement("span");
      name.className = "deck-tile-name";
      name.textContent = this.tileName(board);
      caption.append(number, name);

      // The cover opens every deck, and a chapter's opening page and a
      // board's front page go with their boards (hiding.js): none is the
      // presenter's to hide.
      if (
        this.arranging &&
        slug &&
        slug !== "cover" &&
        !attached(board)
      ) {
        const toggle = document.createElement("button");
        toggle.type = "button";
        toggle.className = "deck-tile-hide deck-label-sm";
        toggle.dataset.slug = slug;
        toggle.dataset.action = "click->presentation#toggleHidden";
        toggle.dataset.hideLabel = bar.hideLabel;
        toggle.dataset.showLabel = bar.showLabel;
        caption.append(toggle);
      }

      board.before(tile);
      tile.append(board, caption);
    });
  },

  // What the board is called: its slide's name; a page of a slide that
  // draws several (a product each) by its own headline, or the pages read
  // "Product Focus 2, 3, 4"; a chapter's opening page by its chapter.
  tileName(board) {
    if ("opener" in board.dataset) return this.chaptersValue[board.dataset.chapter] || board.dataset.chapter || "";
    const slug = board.dataset.slideSlug || "";
    const key = slug.replace(/-\d+$/, "");
    const part = Number(slug.match(/-(\d+)$/)?.[1] || 1);
    const title = this.titlesValue[key];
    const headline = board.querySelector(HEADLINE);
    const said = headline && plainText(headline);
    const several = this.slideTargets.some((other) => other !== board && other.dataset.slideSlug?.replace(/-\d+$/, "") === key);
    if (several && said) return said;
    if (title) return part > 1 ? `${title} ${part}` : title;
    return said || slug;
  },

  // Each board's place in the room as the tiles stand now, a hidden board
  // none, and the bar's count. Read off the tiles, so it is right while
  // one is being carried too.
  renderTiles() {
    if (!this.tiled) return;
    let place = 0;
    let hidden = 0;
    this.canvasTarget.querySelectorAll(".deck-tile").forEach((tile) => {
      const board = tile.querySelector('[data-presentation-target="slide"]');
      const off = board?.dataset.hidden === "true";
      if (off) hidden += !attached(board);
      else place += 1;
      tile.classList.toggle("is-off", off);
      tile.querySelector(".deck-tile-number").textContent = off
        ? "–"
        : String(place).padStart(2, "0");
      const toggle = tile.querySelector(".deck-tile-hide");
      if (toggle) {
        toggle.textContent = off
          ? toggle.dataset.showLabel
          : toggle.dataset.hideLabel;
        toggle.setAttribute("aria-pressed", String(off));
      }
    });

    if (!this.hasOverviewCountTarget) return;
    const bar = this.overviewBarTarget.dataset;
    const shown = (place === 1 ? bar.onShowOne : bar.onShowOther).replace(
      "%{count}",
      place,
    );
    this.overviewCountTarget.textContent = hidden
      ? `${shown} · ${bar.hiddenLabel.replace("%{count}", hidden)}`
      : shown;
  },

  // The tiles as large as they can be with every board on one screen, or,
  // when there are too many for that, at a size still worth reading and
  // the grid scrolls. − and + choose the number of columns instead.
  sizeTiles() {
    if (!this.overview || !this.hasStageTarget) return;
    const room = this.overviewRoom();
    const bar = this.hasOverviewBarTarget
      ? this.overviewBarTarget.offsetHeight
      : 0;
    const height = this.stageTarget.clientHeight - bar - PAD_Y;
    const across = (columns) =>
      Math.floor((room - GAP_X * (columns - 1)) / columns);
    const columns =
      this.chosenColumns() || this.fittedColumns(room, height, across);
    const width = Math.min(Math.max(across(columns), TILE_MIN), TILE_MAX);
    this.overviewColumns = columns;
    this.canvasTarget.style.setProperty("--deck-tile", `${width}px`);
    this.canvasTarget.style.setProperty(
      "--deck-tile-zoom",
      (width / BOARD_WIDTH).toFixed(4),
    );
  },

  // The width the grid has: the canvas's own, laid out as a grid now, less
  // its padding — beside the notes in the presenter window, and less the
  // scrollbar when the grid scrolls.
  overviewRoom() {
    return this.canvasTarget.clientWidth - PAD_X * 2;
  },

  fittedColumns(room, height, across) {
    const boards = this.slideTargets.length;
    for (let columns = 1; columns <= boards; columns++) {
      const width = across(columns);
      if (width < FIT_MIN) break;
      const rows = Math.ceil(boards / columns);
      if (
        rows * ((width * BOARD_HEIGHT) / BOARD_WIDTH + CAPTION) +
          (rows - 1) * GAP_Y <=
        height
      )
        return columns;
    }
    return Math.max(1, Math.floor((room + GAP_X) / (FIT_MIN + GAP_X)));
  },

  // The presenter's own size, kept in this browser. Nothing is lost when
  // the browser keeps nothing: the grid is fitted again.
  chosenColumns() {
    try {
      return Number(window.localStorage.getItem(COLUMNS_KEY)) || null;
    } catch {
      return null;
    }
  },

  overviewSmaller() {
    this.chooseColumns((this.overviewColumns || 1) + 1);
  },

  overviewLarger() {
    this.chooseColumns((this.overviewColumns || 2) - 1);
  },

  chooseColumns(columns) {
    const room = this.overviewRoom();
    const most = Math.max(1, Math.floor((room + GAP_X) / (TILE_MIN + GAP_X)));
    const chosen = Math.min(Math.max(columns, 1), most);
    try {
      window.localStorage.setItem(COLUMNS_KEY, String(chosen));
    } catch {
      // Not kept: this overview still takes the size.
    }
    this.overviewColumns = chosen;
    this.sizeTiles();
    this.slideTargets[this.indexValue]
      ?.closest(".deck-tile")
      ?.scrollIntoView({ block: "nearest" });
  },

  // Back to the grid fitted to the screen.
  overviewFit() {
    try {
      window.localStorage.removeItem(COLUMNS_KEY);
    } catch {
      // Nothing was kept.
    }
    this.sizeTiles();
  },

  // A board let go in a new place: the order saved for this deck, its
  // hidden boards as they are, and the deck drawn again when the overview
  // closes. Not saved, the boards go back where they were.
  async saveOverviewOrder(before) {
    const boards = this.slideTargets.filter(
      (board) =>
        board.dataset.slideSlug &&
        board.dataset.slideSlug !== "cover" &&
        !attached(board),
    );
    const order = [
      ...new Set(
        boards.map((board) => board.dataset.slideSlug.replace(/-\d+$/, "")),
      ),
    ];
    const hidden = boards
      .filter((board) => board.dataset.hidden === "true")
      .map((board) => board.dataset.slideSlug);
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const response = await fetch(this.element.dataset.presentationSettingsUrl, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": token,
      },
      body: JSON.stringify({
        selection: { hidden, order, keep: "deck" },
        sender: this.sender,
      }),
    }).catch(() => null);
    if (response?.ok) {
      this.reordered = true;
      // Closed before the order was saved: drawn again now.
      if (!this.overview) this.reloadHere();
      return true;
    }
    this.canvasTarget.append(...before);
    this.afterReorder();
    this.overviewSay(this.overviewBarTarget.dataset.unsaved);
    return false;
  },

  // The boards have changed places: the board on show is the same board,
  // at its new index. Until they are indexed again, the slugs say where
  // the boards stood before the move.
  afterReorder() {
    const slug = this.slugByIndex[this.indexValue];
    this.indexBoards();
    const index = this.indexBySlug.get(slug);
    if (index !== undefined) this.showSlide(index, false, false);
    this.renderTiles();
  },

  overviewSay(text) {
    if (this.hasOverviewSaidTarget) this.overviewSaidTarget.textContent = text;
  },
};
